import { Drawer as DrawerBase } from '@base-ui/react/drawer'
import { ScrollArea } from '@base-ui/react/scroll-area'
import { Tabs } from '@base-ui/react/tabs'
import clsx from 'clsx'
import toLower from 'lodash/toLower'
import React from 'react'

import { ampli } from '@Analytics/ampli'
import SvgClose from '@Assets/SVG/Close'
import { Blocks, Tab } from '@Components/Drawer/types'
import { PagePresentation } from '@Generated/graphql'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { useTranslation } from '@Globals/Translations'

import styles from './Drawer.module.scss'

const getTabIconURL = (tab: Tab): string | undefined => {
  const asset = tab.appearance?.asset
  if (!asset) {
    return undefined
  }

  if (asset?.__typename === 'Image') {
    return asset.url ?? undefined
  }

  if ('image' in asset) {
    return asset.image?.url ?? undefined
  }

  return undefined
}

const TabPanel = ({
  presentation,
  tab,
}: {
  presentation?: PagePresentation
  tab: Tab
}) => {
  // @ts-expect-error
  const blocks = useBlockComponents(tab.blocks || [], '', false, presentation)

  return (
    <Tabs.Panel className={clsx(styles.panel, styles.blocks)} value={tab.id}>
      {blocks}
    </Tabs.Panel>
  )
}

export const DrawerContentBasic = ({
  children,
  footer,
  title,
}: {
  children: React.ReactNode
  footer?: React.ReactNode
  title?: React.ReactNode
}) => (
  <DrawerBase.Content className={styles.content}>
    <DrawerBase.Close aria-label="Close" className={styles.close}><SvgClose /></DrawerBase.Close>

    {title && (
      <div className={styles.header}>
        <DrawerBase.Title className={styles.title} render={<div />}>{title}</DrawerBase.Title>
      </div>
    )}

    <ScrollArea.Root className={styles.body}>
      <ScrollArea.Viewport className={styles.bodyViewport}>
        <ScrollArea.Content className={clsx(styles.bodyContent, styles.blocks)}>
          {children}
        </ScrollArea.Content>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar className={styles.scrollbar}>
        <ScrollArea.Thumb className={styles.scrollbarThumb} />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>

    {footer && <div className={styles.footer}>{footer}</div>}
  </DrawerBase.Content>
)

export const DrawerContentBlocks = ({
  blocks,
  children,
  footer,
  presentation,
  title,
}: {
  blocks: Blocks
  children?: React.ReactNode
  footer?: React.ReactNode
  presentation?: PagePresentation
  title?: React.ReactNode
}) => {
  // @ts-expect-error
  const components = useBlockComponents(blocks || [], '', false, presentation)

  return (
    <DrawerContentBasic footer={footer} title={title}>
      {components}
      {children}
    </DrawerContentBasic>
  )
}

export const DrawerContentTabs = ({
  children,
  footer,
  pageID,
  pageType,
  presentation,
  tabs,
  title,
}: {
  children?: React.ReactNode
  footer?: React.ReactNode
  pageID?: string
  pageType?: string
  presentation?: PagePresentation
  tabs: Tab[]
  title?: React.ReactNode
}) => (
  <DrawerBase.Content className={styles.content}>
    <DrawerBase.Close aria-label="Close" className={styles.close}><SvgClose /></DrawerBase.Close>

    <Tabs.Root
      className={styles.tabs}
      defaultValue={tabs[0]?.id}
      onValueChange={(value, eventDetails) => {
        const target = eventDetails.event?.target as HTMLElement | undefined
        const root = target?.closest(`.${styles.tabs}`)
        const tab = root?.querySelector<HTMLElement>(`[data-tab-id="${value}"]`)
        const viewport = root?.querySelector<HTMLElement>(`.${styles.bodyViewport}`)
        tab?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' })
        viewport?.scrollTo({ behavior: 'instant', top: 0 })

        if (pageID && pageType) {
          ampli.modalOpen({
            dest_page_type_id: pageID,
            dest_page_type: toLower(pageType),
            tab_id: value,
          })
        }
      }}
    >
      <div className={styles.header}>
        {title && <DrawerBase.Title className={styles.title} render={<div />}>{title}</DrawerBase.Title>}

        <ScrollArea.Root className={styles.tabsScrollArea}>
          <Tabs.List className={clsx(styles.list, styles.tabsScrollViewport)} render={<ScrollArea.Viewport />}>
            {tabs.map((tab) => {
              const iconURL = getTabIconURL(tab)
              return (
                <Tabs.Tab key={tab.id} className={styles.tab} data-tab-id={tab.id} value={tab.id}>
                  {iconURL && <span className={styles.tabIcon} style={{ WebkitMaskImage: `url(${iconURL})`, maskImage: `url(${iconURL})` }} />}
                  {tab.title}
                </Tabs.Tab>
              )
            })}
          </Tabs.List>
          <ScrollArea.Scrollbar className={clsx(styles.scrollbar, styles.scrollbarThin, styles.scrollbarIndicator)} orientation="horizontal">
            <ScrollArea.Thumb className={styles.scrollbarThumb} />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      </div>

      <ScrollArea.Root className={styles.body}>
        <ScrollArea.Viewport className={styles.bodyViewport}>
          <ScrollArea.Content className={styles.bodyContent}>
            {tabs.map(tab => <TabPanel key={tab.id} presentation={presentation} tab={tab} />)}
            {children}
          </ScrollArea.Content>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar className={styles.scrollbar}>
          <ScrollArea.Thumb className={styles.scrollbarThumb} />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>
    </Tabs.Root>

    {footer && <div className={styles.footer}>{footer}</div>}
  </DrawerBase.Content>
)

export const DrawerContentError = () => {
  const { t } = useTranslation()

  return (
    <DrawerBase.Content className={styles.content}>
      <DrawerBase.Close aria-label="Close" className={styles.close}>
        <SvgClose />
      </DrawerBase.Close>

      <div className={styles.header}>
        <DrawerBase.Title className={styles.title} render={<div />}>
          {t('common:ERROR_LABEL')}
        </DrawerBase.Title>
      </div>

      <div className={styles.body}>
        {t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')}
      </div>

      <div className={styles.footer}>
        <DrawerBase.Close className={styles.button}>
          {t('common:CLOSE')}
        </DrawerBase.Close>
      </div>
    </DrawerBase.Content>
  )
}

export const DrawerContent = ({
  blocks,
  children,
  footer,
  pageID,
  pageType,
  presentation,
  showError,
  tabs,
  title,
}: {
  blocks?: Blocks
  children?: React.ReactNode
  footer?: React.ReactNode
  pageID?: string
  pageType?: string
  presentation?: PagePresentation
  showError?: boolean
  tabs?: Tab[]
  title?: React.ReactNode
}) => {
  if (showError) {
    return <DrawerContentError />
  }

  if (tabs && tabs.length !== 0) {
    return (
      <DrawerContentTabs
        footer={footer}
        pageID={pageID}
        pageType={pageType}
        presentation={presentation}
        tabs={tabs}
        title={title}
      >
        {children}
      </DrawerContentTabs>
    )
  }

  if (blocks && blocks.length !== 0) {
    return (
      <DrawerContentBlocks
        blocks={blocks}
        footer={footer}
        presentation={presentation}
        title={title}
      >
        {children}
      </DrawerContentBlocks>
    )
  }

  if (children) {
    return (
      <DrawerContentBasic footer={footer} title={title}>
        {children}
      </DrawerContentBasic>
    )
  }

  return null
}
